import { ref, watch, isRef, isReactive, unref, type Ref } from 'vue'
import type { AxiosRequestConfig } from 'axios'
import { http } from '@/libs'
import type { HttpInstance } from '@/libs/http'
import { message } from 'ant-design-vue'
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'
import type { FetchDataParams } from '@/types'

/** —— 可选增强项：对接 http.call 的额外参数 —— */
interface ExtraOptions {
  /** json | urlencoded | formdata（默认 json） */
  contentType?: 'json' | 'urlencoded' | 'formdata'
  /** 透传 axios 配置，如 headers/timeout/signal 等 */
  requestConfig?: AxiosRequestConfig
  /** 发起新请求前是否取消上一请求（默认 true，省流量，避免后到先覆盖） */
  cancelPrevious?: boolean
}

/** —— 若你旧项目还在用 RequestParams，这里用通用替代，避免到处改类型 —— */
type AnyParams = Record<string, unknown>

export function useFetch<
  R = unknown, // 后端返回类型（未 transform 前）
  T = R, // 对外暴露类型（transform 后）
  P extends object = AnyParams, // 入参类型
>(params: FetchDataParams<R, T, P> & Partial<ExtraOptions>) {
  const {
    url,
    transform,
    initialData,
    params: initialParams = {} as P,
    method = 'POST',
    debounceTime = 0,
    errorMessage = '请求出错',
    showErrorMessage = false,
    autoFetch = true,
    onSuccess,
    onComplete,
    onFailure,
    // —— 新增三个可选增强项 ——
    contentType = 'json',
    requestConfig,
    cancelPrevious = true,
  } = params

  const loading = ref(false)
  const data = ref<R | T | null>(initialData ?? null) as Ref<R | T | null>

  // —— 做一次“初始快照”，reset 时用于恢复 ——
  const _initialParamsSnapshot = cloneDeep(unref(initialParams)) as P
  const _initialDataSnapshot = cloneDeep(initialData ?? null)

  // —— 支持传入 ref/reactive 的 params；否则封装成 ref ——
  const queryParams =
    isRef(initialParams) || isReactive(initialParams) ? initialParams : ref<P>(initialParams)

  /** —— 并发安全：自增请求编号，只允许最后一次请求回写 —— */
  let requestId = 0

  /** —— 为“取消上一请求”准备的外部 AbortController —— */
  let currentController: AbortController | null = null

  const fetchData = async (overrideParams?: P): Promise<R | T | null> => {
    const myId = ++requestId // 本次请求编号

    // —— 如需取消上一请求，先中断它，避免浪费流量/后到覆盖 ——
    if (cancelPrevious && currentController) {
      currentController.abort()
      currentController = null
    }

    // —— 为本次请求创建新的 AbortController，并透传给 http.call ——
    currentController = new AbortController()

    loading.value = true
    try {
      const actualParams = overrideParams ? unref(overrideParams) : unref(queryParams)

      // —— 统一使用 http.call；GET 走 params，其它方法走 data；支持 contentType/requestConfig ——
      const methodUpper = (method || 'POST').toUpperCase()
      const callOpts =
        methodUpper === 'GET'
          ? {
              url,
              method: 'GET' as const,
              params: actualParams,
              contentType,
              requestConfig: { ...requestConfig, signal: currentController.signal },
            }
          : {
              url,
              method: methodUpper as any,
              data: actualParams,
              contentType,
              requestConfig: { ...requestConfig, signal: currentController.signal },
            }

      const responseData = await (http as HttpInstance).call<R>(callOpts)

      // —— 可选数据整形；类型外抛为 T ——
      const transformed = (transform ? transform(responseData) : responseData) as R | T

      // —— 仅最后一次请求允许写入，防止“后到先覆盖” ——
      if (myId === requestId) {
        data.value = transformed
        onSuccess?.(transformed)
      }
      return transformed
    } catch (error) {
      console.error(`请求 ${url} 出错:`, error)

      // —— 统一错误消息；优先用 Error.message ——
      // 注意：错误已在 parsers.ts 中统一处理，showErrorMessage 已废弃，保留参数是为了向后兼容
      const errorMsg = error instanceof Error ? error.message : `${errorMessage}：${String(error)}`
      // if (showErrorMessage) message.error(errorMsg) // 已废弃：避免重复提示
      onFailure?.(new Error(errorMsg))

      if (myId === requestId) {
        data.value = null
      }
      return null
    } finally {
      // —— 仅最后一次请求结束后关闭 loading；并清理本地 controller 引用 ——
      if (myId === requestId) {
        loading.value = false
        currentController = null
      }
      onComplete?.()
    }
  }

  // —— 可选防抖 ——
  let executeFetch: typeof fetchData
  if (debounceTime) {
    const debounced = debounce((p: P | undefined, resolve: (v: R | T | null) => void) => {
      fetchData(p).then(resolve)
    }, debounceTime)
    executeFetch = (p?: P) => new Promise<R | T | null>((resolve) => debounced(p, resolve))
  } else {
    executeFetch = fetchData
  }

  /** —— 重置 queryParams 与 data，支持可选立即 refetch —— */
  const reset = (options: { refetch?: boolean } = {}) => {
    const { refetch = false } = options

    // —— 还原 queryParams（兼容 ref 与 reactive 对象） ——
    if (isRef(queryParams)) {
      ;(queryParams as Ref<P>).value = cloneDeep(_initialParamsSnapshot) as P
    } else {
      const qp = queryParams as P
      Object.keys(qp).forEach((k) => {
        ;(qp as any)[k] = cloneDeep((_initialParamsSnapshot as any)[k])
      })
    }

    // —— 还原 data 与状态；必要时触发一次拉取 ——
    data.value = cloneDeep(_initialDataSnapshot) as any
    loading.value = false
    if (refetch) fetchData()
  }

  /** —— 初始化：按需自动请求 —— */
  if (autoFetch) {
    fetchData()
  }

  // —— 监听参数变化自动请求；deep 保证嵌套变动也能触发 ——
  watch(
    () => unref(queryParams),
    () => {
      fetchData()
    },
    { deep: true },
  )

  return {
    data,
    loading,
    queryParams,
    fetchData: executeFetch,
    reset,
  }
}
